import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useFormik } from "formik";
import * as yup from "yup";
import { Icons } from "assets";
import CounterButton from "shared/components/counterButton";
import CustomButton from "shared/components/customButton";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import { editLimit } from "shared/services/adminService";
import styles from "./style.module.scss";

const todayISO = () => new Date().toISOString().split("T")[0];

const MAX_LIMIT = 999999;

const BLOCKED_NUMBER_KEYS = ["e", "E", "+", "-", ".", ","];

const sanitizeCount = (raw: string): number => {
  const digitsOnly = raw.replace(/\D/g, "");
  const stripped = digitsOnly.replace(/^0+(?=\d)/, "");
  if (stripped === "") return 0;
  const next = Number(stripped);
  return next > MAX_LIMIT ? MAX_LIMIT : next;
};

const validationSchema = yup.object().shape({
  students_count: yup
    .number()
    .typeError("Must be a valid number")
    .required("Students count is required")
    .min(1, "Must be at least 1"),
  teachers_count: yup
    .number()
    .typeError("Must be a valid number")
    .required("Teachers count is required")
    .min(1, "Must be at least 1"),
  expiry_date: yup
    .string()
    .required("Expiry date is required")
    .test(
      "is-future",
      "Expiry date must be after today",
      (value) => !!value && value > todayISO()
    )
});

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface BookRow {
  id: number;
  language_id: string;
  age_group_id: string;
  book_count: string;
  // true once the row was confirmed via the "+" button or loaded
  // from the backend.
  committed: boolean;
}

interface Props {
  open: boolean;
  handleClose: () => void;
  item: any;
  languages: any[];
  ageGroups: any[];
  bookFilterCounts: any[];
  onSuccess: () => void;
}

export default function UpdateSchoolPlan({
  open,
  handleClose,
  item,
  languages,
  ageGroups,
  bookFilterCounts,
  onSuccess
}: Props) {
  const [loader, setLoader] = useState<boolean>(false);
  const emptyRow = (): BookRow => ({
    id: Date.now() + Math.random(),
    language_id: "",
    age_group_id: "",
    book_count: "",
    committed: false
  });

  const [bookRows, setBookRows] = useState<BookRow[]>([emptyRow()]);
  const [booksError, setBooksError] = useState<string>("");
  const [booksTouched, setBooksTouched] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    const books = item?.library_books;
    if (Array.isArray(books) && books.length > 0) {
      // Existing plan: backend rows are already committed. A single
      // empty row is appended so the admin can add more.
      setBookRows([
        ...books.map((b: any, i: number) => ({
          id: Date.now() + i,
          language_id: b?.language_id != null ? String(b.language_id) : "",
          age_group_id:
            b?.age_range_id != null ? String(b.age_range_id) : "",
          book_count: b?.count != null ? String(b.count) : "",
          committed: true
        })),
        emptyRow()
      ]);
    } else {
      setBookRows([emptyRow()]);
    }
    setBooksError("");
    setBooksTouched(false);
    // eslint-disable-next-line
  }, [open, item]);

  // A row is complete only when language + age are chosen and the count
  // is a positive number (no empty, 0 or negative).
  const isRowComplete = (row: BookRow) =>
    Boolean(row.language_id && row.age_group_id) &&
    Number(row.book_count) > 0;

  // A row with none of its fields filled (e.g. a freshly added row that
  // the user did not fill in). These are skipped, not required.
  const isRowEmpty = (row: BookRow) =>
    !row.language_id && !row.age_group_id && !row.book_count;

  // Returns the first row whose language + age combination repeats an earlier
  // row. The same language with a different age (or vice versa) is allowed.
  const findDuplicateRow = (rows: BookRow[]) => {
    const seen = new Set<string>();
    for (const r of rows) {
      if (!r.language_id || !r.age_group_id) continue;
      const key = `${r.language_id}-${r.age_group_id}`;
      if (seen.has(key)) return r;
      seen.add(key);
    }
    return null;
  };

  // Books available for a given language + age combination, as reported
  // by the books/filter-counts endpoint. Returns null when the combo
  // isn't selected yet or isn't present in the data.
  const getAvailableBooks = (
    languageId: string,
    ageGroupId: string
  ): number | null => {
    if (!languageId || !ageGroupId) return null;
    const match = bookFilterCounts.find(
      (c) =>
        String(c.language_id) === String(languageId) &&
        String(c.age_range_id) === String(ageGroupId)
    );
    return match ? Number(match.books_count) : null;
  };

  // A row whose requested count is greater than the books available for
  // its selected language + age combination.
  const rowExceedsMax = (row: BookRow): boolean => {
    if (!row.language_id || !row.age_group_id || !row.book_count) return false;
    const available = getAvailableBooks(row.language_id, row.age_group_id);
    if (available == null) return false;
    return Number(row.book_count) > available;
  };

  // A row whose count has been typed but is not a positive number
  // (i.e. 0 or a negative value).
  const rowCountNotPositive = (row: BookRow): boolean =>
    row.book_count.trim() !== "" && Number(row.book_count) <= 0;

  const DUPLICATE_ERROR = "This language and age group is already added.";
  const MAX_ERROR =
    "The number of books exceeds what is available for the selected language & age.";

  const addRow = () => {
    if (findDuplicateRow(bookRows)) {
      setBooksError(DUPLICATE_ERROR);
      return;
    }
    const lastRow = bookRows[bookRows.length - 1];
    if (lastRow && rowExceedsMax(lastRow)) {
      setBooksError(MAX_ERROR);
      return;
    }
    setBookRows((prev) => [
      // committing the last row is what makes it eligible for the payload
      ...prev.map((r, i) =>
        i === prev.length - 1 ? { ...r, committed: true } : r
      ),
      emptyRow()
    ]);
    setBooksError("");
  };

  const deleteRow = (rowId: number) => {
    setBookRows((prev) => {
      const next = prev.filter((r) => r.id !== rowId);
      setBooksError(findDuplicateRow(next) ? DUPLICATE_ERROR : "");
      return next;
    });
  };

  const updateRow = (rowId: number, field: keyof BookRow, value: string) => {
    const next = bookRows.map((r) => {
      if (r.id !== rowId) return r;
      const updated = { ...r, [field]: value };
      if (field === "language_id" || field === "age_group_id") {
        updated.book_count = "";
      }
      return updated;
    });
    setBookRows(next);
    setBooksError(findDuplicateRow(next) ? DUPLICATE_ERROR : "");
    setBooksTouched(false);
  };

  // Only committed rows count toward the total / payload.
  const totalBooks = bookRows.reduce(
    (sum, r) => (r.committed ? sum + (Number(r.book_count) || 0) : sum),
    0
  );

  // The row that repeats an earlier language + age combination. Its
  // Language / Age selects are highlighted with a red border.
  const duplicateRow = findDuplicateRow(bookRows);
  const hasDuplicateRow = Boolean(duplicateRow);
  // Only committed rows (added via the "+" button or loaded from the
  // backend) count toward the plan. A row the user has started filling
  // but not yet added is ignored entirely — it does not block Update
  // and is never sent to the API.
  const committedRows = bookRows.filter((r) => r.committed);
  const hasCommittedRow = committedRows.length > 0;
  const allCommittedRowsValid =
    committedRows.every(isRowComplete) &&
    !findDuplicateRow(committedRows) &&
    !committedRows.some(rowExceedsMax);
  // Update is allowed only when at least one row has been added and all
  // added rows are complete and valid.
  const disableUpdate = !hasCommittedRow || !allCommittedRowsValid;

  const defaultExpiry = (() => {
    if (item?.plan_expiry_date) return item.plan_expiry_date;
    const d = new Date();
    d.setDate(d.getDate() + (Number(item?.duration) || 30));
    return d.toISOString().split("T")[0];
  })();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      students_count:
        Number(item?.school?.students_limit ?? item?.students_count) || 1,
      teachers_count:
        Number(item?.school?.teachers_limit ?? item?.teachers_count) || 1,
      expiry_date: defaultExpiry
    },
    validationSchema,
    onSubmit: (values) => {
      setBooksTouched(true);
      // A row is only "filled" once it has been added via the "+" button
      // (or loaded from the backend). A typed-but-not-added row is
      // ignored entirely (not sent).
      const filledRows = bookRows.filter((r) => r.committed);
      if (filledRows.length === 0) {
        setBooksError("Please add at least one book using the + button.");
        return;
      }
      const incompleteRow = filledRows.find((r) => !isRowComplete(r));
      if (incompleteRow) {
        setBooksError("Please fill all fields in every book row.");
        return;
      }
      if (findDuplicateRow(filledRows)) {
        setBooksError(DUPLICATE_ERROR);
        return;
      }
      if (filledRows.some(rowExceedsMax)) {
        setBooksError(MAX_ERROR);
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiry = new Date(values.expiry_date);
      expiry.setHours(0, 0, 0, 0);
      const durationDays = Math.max(
        1,
        Math.round((expiry.getTime() - today.getTime()) / 86400000)
      );

      const booksPayload = filledRows.map((row) => ({
        language_id: Number(row.language_id),
        age_range_id: Number(row.age_group_id),
        count: Number(row.book_count)
      }));

      setLoader(true);
      editLimit({
        school_id: item?.id,
        teachers_limit: values.teachers_count,
        students_limit: values.students_count,
        expiry_date: values.expiry_date,
        demo_duration_days: durationDays,
        is_multi_campus: 0,
        library_books: JSON.stringify(booksPayload)
      })
        .then(({ data }) => {
          toastMessage("success", data?.message);
          setLoader(false);
          onClose();
          onSuccess();
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message);
          setLoader(false);
        });
    }
  });

  const onClose = () => {
    formik.resetForm();
    setBooksError("");
    setBooksTouched(false);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="update-school-plan-dialog"
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { mx: { xs: 1.5, sm: 3 } } }}
    >
      <DialogContent>
        <Icons.CrossIcon className={styles.crossIcon} onClick={onClose} />

        <div className={styles.modalTitle}>Update School Plan</div>

        <div>
          {/* OLD CounterButton-based inputs — kept for reference, not rendered */}
          {false && (
            <>
              <div className={styles.counterWrapper}>
                <CounterButton
                  counter={formik.values.students_count}
                  setCounter={(next: any) => {
                    const value =
                      typeof next === "function"
                        ? next(formik.values.students_count)
                        : next;
                    formik.setFieldValue("students_count", value);
                  }}
                />
              </div>
              <div className={styles.counterWrapper}>
                <CounterButton
                  counter={formik.values.teachers_count}
                  setCounter={(next: any) => {
                    const value =
                      typeof next === "function"
                        ? next(formik.values.teachers_count)
                        : next;
                    formik.setFieldValue("teachers_count", value);
                  }}
                />
              </div>
            </>
          )}

          {/* NEW custom −/+ input: minus on the left, bordered input in the middle, plus on the right */}
          <div className={styles.twoColField}>
            <div>
              <label className={styles.fieldLabel}>Students</label>
              <div className={styles.numberRow}>
                <button
                  type="button"
                  className={styles.stepBtn}
                  aria-label="Decrease students"
                  onClick={() => {
                    const current = Number(formik.values.students_count);
                    formik.setFieldValue(
                      "students_count",
                      !current || current <= 1 ? 1 : current - 1
                    );
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </button>
                <input
                  type="number"
                  inputMode="numeric"
                  aria-label="Students count"
                  className={styles.numberInput}
                  value={
                    formik.values.students_count === 0
                      ? ""
                      : formik.values.students_count
                  }
                  min={1}
                  max={MAX_LIMIT}
                  onKeyDown={(e) => {
                    if (BLOCKED_NUMBER_KEYS.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "students_count",
                      sanitizeCount(e.target.value)
                    )
                  }
                />
                <button
                  type="button"
                  className={styles.stepBtn}
                  aria-label="Increase students"
                  onClick={() => {
                    const current = Number(formik.values.students_count);
                    formik.setFieldValue(
                      "students_count",
                      current >= MAX_LIMIT ? MAX_LIMIT : current + 1
                    );
                  }}
                >
                  <AddIcon fontSize="small" />
                </button>
              </div>
              {formik.touched.students_count &&
                formik.errors.students_count && (
                  <div className={styles.errorText}>
                    {formik.errors.students_count as string}
                  </div>
                )}
            </div>

            <div>
              <label className={styles.fieldLabel}>Teachers</label>
              <div className={styles.numberRow}>
                <button
                  type="button"
                  className={styles.stepBtn}
                  aria-label="Decrease teachers"
                  onClick={() => {
                    const current = Number(formik.values.teachers_count);
                    formik.setFieldValue(
                      "teachers_count",
                      !current || current <= 1 ? 1 : current - 1
                    );
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </button>
                <input
                  type="number"
                  inputMode="numeric"
                  aria-label="Teachers count"
                  className={styles.numberInput}
                  value={
                    formik.values.teachers_count === 0
                      ? ""
                      : formik.values.teachers_count
                  }
                  min={1}
                  max={MAX_LIMIT}
                  onKeyDown={(e) => {
                    if (BLOCKED_NUMBER_KEYS.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "teachers_count",
                      sanitizeCount(e.target.value)
                    )
                  }
                />
                <button
                  type="button"
                  className={styles.stepBtn}
                  aria-label="Increase teachers"
                  onClick={() => {
                    const current = Number(formik.values.teachers_count);
                    formik.setFieldValue(
                      "teachers_count",
                      current >= MAX_LIMIT ? MAX_LIMIT : current + 1
                    );
                  }}
                >
                  <AddIcon fontSize="small" />
                </button>
              </div>
              {formik.touched.teachers_count &&
                formik.errors.teachers_count && (
                  <div className={styles.errorText}>
                    {formik.errors.teachers_count as string}
                  </div>
                )}
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Expiry Date</label>
            <input
              type="date"
              aria-label="Expiry Date"
              className={styles.dateInput}
              value={formik.values.expiry_date}
              min={todayISO()}
              onChange={(e) =>
                formik.setFieldValue("expiry_date", e.target.value)
              }
              onBlur={() => formik.setFieldTouched("expiry_date", true)}
            />
            {formik.touched.expiry_date && formik.errors.expiry_date && (
              <div className={styles.errorText}>
                {formik.errors.expiry_date as string}
              </div>
            )}
          </div>

          <div className={styles.booksSection}>
            <div className={styles.sectionTitle}>Books</div>

            {booksError && (
              <div className={styles.booksError}>{booksError}</div>
            )}

            {bookRows.map((row, index) => (
              <div key={row.id} className={styles.bookRow}>
                <div className={styles.bookField}>
                  <select
                    className={`${styles.bookSelect} ${
                      duplicateRow?.id === row.id ? styles.errorBorder : ""
                    }`}
                    aria-label="Language"
                    value={row.language_id}
                    onChange={(e) =>
                      updateRow(row.id, "language_id", e.target.value)
                    }
                  >
                    <option value="">Language</option>
                    {languages.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                  {booksTouched && !isRowEmpty(row) && !row.language_id && (
                    <div className={styles.fieldError}>
                      Language is required
                    </div>
                  )}
                </div>

                <div className={styles.bookField}>
                  <select
                    className={`${styles.bookSelect} ${
                      duplicateRow?.id === row.id ? styles.errorBorder : ""
                    }`}
                    aria-label="Age Group"
                    value={row.age_group_id}
                    onChange={(e) =>
                      updateRow(row.id, "age_group_id", e.target.value)
                    }
                  >
                    <option value="">Age Group</option>
                    {ageGroups.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.text}
                      </option>
                    ))}
                  </select>
                  {booksTouched && !isRowEmpty(row) && !row.age_group_id && (
                    <div className={styles.fieldError}>
                      Age group is required
                    </div>
                  )}
                </div>

                <div className={styles.bookField}>
                  {(() => {
                    const available = getAvailableBooks(row.language_id, row.age_group_id);
                    const showBadge = Boolean(row.language_id && row.age_group_id && available != null);
                    return (
                      <div className={styles.bookInputWrapper}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className={`${styles.bookInput} ${
                            rowExceedsMax(row) || rowCountNotPositive(row) ? styles.errorBorder : ""
                          } ${showBadge ? styles.bookInputWithBadge : ""}`}
                          placeholder="No. of Books"
                          value={row.book_count}
                          onChange={(e) => {
                            let val = e.target.value.replace(/[^0-9]/g, "");
                            if (val === "0") val = "1";
                            if (available != null && val !== "" && Number(val) > available) {
                              val = String(available);
                            }
                            updateRow(row.id, "book_count", val);
                          }}
                        />
                        {showBadge && (
                          <span className={styles.bookAvailable}>
                            Out of {available}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {booksTouched && !isRowEmpty(row) && !row.book_count && (
                    <div className={styles.fieldError}>
                      No. of books is required
                    </div>
                  )}
                  {rowExceedsMax(row) && (
                    <div className={styles.fieldError}>
                      Only{" "}
                      {getAvailableBooks(row.language_id, row.age_group_id)}{" "}
                      available for this language &amp; age
                    </div>
                  )}
                  {rowCountNotPositive(row) && (
                    <div className={styles.fieldError}>
                      Number of books must be greater than 0
                    </div>
                  )}
                </div>

                <div className={styles.bookActions}>
                  {row.committed && index < bookRows.length - 1 && (
                    <button
                      type="button"
                      className={styles.deleteRowBtn}
                      onClick={() => deleteRow(row.id)}
                      title="Remove row"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </button>
                  )}
                  {index === bookRows.length - 1 && (
                    <button
                      type="button"
                      className={styles.addRowBtn}
                      disabled={
                        !isRowComplete(row) ||
                        hasDuplicateRow ||
                        rowExceedsMax(row)
                      }
                      onClick={addRow}
                      title="Add row"
                    >
                      <AddIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </div>
            ))}

            <hr className={styles.booksDivider} />
            <div className={styles.totalRow}>
              Total Books: <strong>{totalBooks}</strong>
            </div>
          </div>

          <div className={styles.btnContainer}>
            <CustomButton
              type="button"
              text="Cancel"
              buttonStyle={styles.btnInsideOut}
              handleClick={onClose}
            />
            <CustomButton
              type="button"
              text="Update"
              buttonStyle={styles.btnCustom}
              handleClick={() => formik.handleSubmit()}
              disabled={disableUpdate}
            />
          </div>
        </div>
      </DialogContent>

      {loader && <Loader />}
    </Dialog>
  );
}
